import { useState } from "react";
import { controlToastState, Modal, TextInput } from "risa-oasis-ui_v2";
import {
  PA_STATUS_OPTIONS,
  ROUTE_VIA_OPTIONS,
} from "../../../constants/oncologySubmission";
import { ModalId } from "../../../enums/modalId";
import { PaStatus } from "../../../enums/paStatus";
import BadgeDropdown from "../../badgeDropdown/badgeDropdown";
import LabelValueRow from "../../labelValueRow/labelValueRow";
import { onAsteraSave } from "./actionHandlers/onAsteraSave";
import {
  AsteraSubmissionDataKeys,
  useAsteraSubmissionData,
} from "./hooks/useAsteraSubmissionData";
import { buildAsteraSubmissionInitialData } from "./utils/buildAsteraSubmissionInitialData";
import {
  getNestedOptionsForPaStatusBucket,
  resolvePaOutcomeClassificationForSave,
} from "./utils/getNestedOptions";

interface AsterasubmissionModalProps {
  onClose: () => void;
  onSuccess?: () => void;
  metaData: {
    data: any;
  };
}

const AsterasubmissionModal = (props: AsterasubmissionModalProps) => {
  const { data } = props.metaData;

  const initialData = data ? buildAsteraSubmissionInitialData(data) : undefined;

  const {
    asteraSubmissionData,
    handleAsteraSubmissionDataChange,
    handleKeyChange,
    handleRouteViaChange,
    handlePaStatusChange,
    initialKey,
    isKeyChanged,
  } = useAsteraSubmissionData(initialData);

  const [resetCounter, setResetCounter] = useState<{
    paStatus: number;
    caseStatus: number;
    comments: number;
  }>({
    paStatus: 0,
    caseStatus: 0,
    comments: 0,
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSave = async () => {
    setIsLoading(true);
    await onAsteraSave({
      data,
      routeVia: asteraSubmissionData.routeVia,
      response_status: asteraSubmissionData.paStatus,
      pa_outcome_classification: resolvePaOutcomeClassificationForSave(
        asteraSubmissionData.paStatus,
        asteraSubmissionData.caseStatus,
      ),
      key: asteraSubmissionData.key,
      comments: asteraSubmissionData.comments,
      // Second STP Status has no field in this modal, so it can only be cleared explicitly —
      // and it has to be, since a new key means the old key's STP outcome no longer applies.
      // Only when the row HAD a key: entering the first one leaves nothing stale behind, and
      // blanking would wipe the "Not Started" written at ingest (the backend's own key-change
      // trigger skips this case too, since _CMM_CHANGED requires a non-NULL old key).
      clearSecondStpStatus: isKeyChanged && Boolean(initialKey),
      onSuccess: () => {
        controlToastState("astera-submission-save-success");
        props.onSuccess?.();
        props.onClose();
      },
      onError: (error) => {
        console.error("Error saving Astera submission:", error);
        controlToastState("astera-submission-save-error");
      },
    });
    setIsLoading(false);
  };

  return (
    <Modal
      dialogId={ModalId.ASTERA_SUBMISSION_MODAL}
      title="Submit Onco"
      onClose={props.onClose}
      onCancel={props.onClose}
      onSave={handleSave}
      disableSave={isLoading}
      saveButtonText={isLoading ? "Submitting..." : "Submit"}
      cancelText="Cancel"
    >
      <div className="flex flex-col gap-1">
        <div className="flex flex-col gap-2">
          <LabelValueRow label="Patient Name:">
            <div className="text-sm font-normal text-primaryGray-2">
              {data?.patientName?.mainText || data?.patient_name || ""}
            </div>
          </LabelValueRow>

          <LabelValueRow label="Drug:">
            <div className="text-sm font-normal text-primaryGray-2">
              {data?.drugName || data?.drug_name || data?.drug || ""}
            </div>
          </LabelValueRow>

          <LabelValueRow label="Key:">
            <TextInput
              id="key-input"
              label="Key"
              hideLabel={true}
              defaultValue={asteraSubmissionData.key}
              onChange={(e) => {
                handleKeyChange(e.value);
                const isRestoringOriginalKey = e.value === initialKey;
                setResetCounter((prev) => ({
                  // Only blank the badges when the key really changed. badgeDropdown declares its
                  // resetCounter effect after the defaultBadge one, so on the restore path a bump
                  // would run last and blank a status the hook just put back — leaving the user
                  // looking at empty dropdowns while Submit still posts the restored values.
                  paStatus: isRestoringOriginalKey
                    ? prev.paStatus
                    : prev.paStatus + 1,
                  caseStatus: isRestoringOriginalKey
                    ? prev.caseStatus
                    : prev.caseStatus + 1,
                  // Comments is uncontrolled, so it has to remount in BOTH directions to show
                  // whatever the hook just wrote (cleared, or restored).
                  comments: prev.comments + 1,
                }));
              }}
            />
          </LabelValueRow>
        </div>

        <div className="flex flex-col justify-center gap-2">
          <LabelValueRow label="Route Via:">
            <BadgeDropdown
              badgeList={ROUTE_VIA_OPTIONS}
              defaultBadge={
                ROUTE_VIA_OPTIONS.find(
                  (b) => b.id === asteraSubmissionData.routeVia,
                ) ?? null
              }
              showBorder={true}
              borderColor="#A8A8A8"
              onClick={(id: string) => {
                handleRouteViaChange(id);
                setResetCounter((prev) => ({
                  ...prev,
                  paStatus: prev.paStatus + 1,
                  caseStatus: prev.caseStatus + 1,
                }));
              }}
            />
          </LabelValueRow>

          <LabelValueRow label="PA Status:">
            <BadgeDropdown
              badgeList={PA_STATUS_OPTIONS}
              defaultBadge={
                PA_STATUS_OPTIONS.find(
                  (b) => b.id === asteraSubmissionData.paStatus,
                ) ?? null
              }
              showBorder={true}
              borderColor="#A8A8A8"
              onClick={(id: string) => {
                handlePaStatusChange(id);
                setResetCounter((prev) => ({
                  ...prev,
                  caseStatus: prev.caseStatus + 1,
                }));
              }}
              resetCounter={resetCounter.paStatus}
            />
          </LabelValueRow>

          <LabelValueRow label="PA Status Reason:">
            <BadgeDropdown
              badgeList={getNestedOptionsForPaStatusBucket(
                asteraSubmissionData.paStatus as PaStatus,
              )}
              defaultBadge={
                getNestedOptionsForPaStatusBucket(
                  asteraSubmissionData.paStatus as PaStatus,
                ).find((b) => b.id === asteraSubmissionData.caseStatus) ?? null
              }
              showBorder={true}
              borderColor="#A8A8A8"
              onClick={(id: string) => {
                handleAsteraSubmissionDataChange(
                  AsteraSubmissionDataKeys.CASE_STATUS,
                  id,
                );
              }}
              resetCounter={resetCounter.caseStatus}
            />
          </LabelValueRow>
        </div>
        <div className="my-2">
          <TextInput
            // Uncontrolled (defaultValue), so remount it when a key change clears the comment —
            // otherwise the box would still show the old key's text while state holds "".
            key={`submission-summary-input-${resetCounter.comments}`}
            label="Comments"
            placeholder="Enter submission summary"
            required={false}
            id="submission-summary-input"
            defaultValue={asteraSubmissionData.comments}
            onChange={(e) =>
              handleAsteraSubmissionDataChange("comments", e.value)
            }
          />
        </div>
      </div>
    </Modal>
  );
};

export default AsterasubmissionModal;
